import type { Hit } from 'instantsearch.js';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { CMSSection } from '@/components/Cms/CMSSection';
import type { EndImageProps } from '@/components/CollectionGroup/components/EndImage';
import { Container } from '@/components/Container';
import type { ContentfulBannerHeroFragment } from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import type { WatchHit } from '@/integrations/algolia/types';

import { BannerHero } from './components/BannerHero';
import { Heros } from './components/Heros';
import type { ProductReferencesProps } from './components/ScrollableRow';
import ScrollableRow from './components/ScrollableRow';
import type { CollectionGroupProps } from './types';

import * as S from './CollectionsGroup.styles';

const CollectionGroup: FC<CollectionGroupProps> = ({
  collectionGroupItems,
}) => {
  const isMobileView = useIsMdDown();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      {collectionGroupItems.map((group) => {
        const { banner, endImage, hits, productReferences, sys, type } = group;
        const startIndex = (() => {
          if (!mounted)
            return type?.toUpperCase() === 'TWO_HERO_PRODUCTS' ? 2 : 1; // Default value for SSR
          if (isMobileView) return 0;
          if (type?.toUpperCase() === 'TWO_HERO_PRODUCTS') return 2;
          return 1;
        })();

        const content = (
          <CMSSection key={sys.id} data-testid='CollectionGroupSection'>
            <Container maxWidth='lg'>
              <S.Row>
                {banner && banner.__typename === 'BannerHero' && (
                  <BannerHero
                    banner={banner as ContentfulBannerHeroFragment}
                    data-test-id='CollectionGroupBannerHero'
                    type={type}
                  />
                )}
                <Heros hits={hits as Hit<WatchHit>[]} type={type} />
              </S.Row>
              <ScrollableRow
                endImage={endImage as EndImageProps}
                hits={hits}
                productReferences={productReferences as ProductReferencesProps}
                products={[]}
                startIndex={startIndex}
              />
            </Container>
          </CMSSection>
        );

        if (productReferences?.__typename === 'AutomaticCollectionGroup') {
          return (
            <div key={sys.id} data-sort-behavior='automatic'>
              {content}
            </div>
          );
        }

        return content;
      })}
    </>
  );
};

export default CollectionGroup;
